import React, { useEffect, useRef, useState } from 'react';
import EnquireModal from '../components/Popup/EnquireRoomPopup';
import RoomDetailsModal from '../components/Popup/RoomDetailsPopup';
import { useHotelContext } from '../contexts/HotelContext';
import { Deal, PropertyAvailabilityResponse, PropertyRoom } from '../../../backend/src/models/getPropertyAvailability';
import { fetchHotelBySlug, fetchPropertyDetails } from '../api-client';
import { Accommodation, PropertyDetails } from '../../../backend/src/models/getPropertyDetails';
import { useQuery } from 'react-query';

interface RoomsAndPriceProps {
  room: PropertyRoom;
  onSelect: (room: PropertyRoom, deal: Deal) => void;
  hotelName: string;
  slug: string;
  hotelId: string;
  totalCount: number;
  rate: number;
  availabilityData: PropertyAvailabilityResponse;
}

const RoomsAndPrice: React.FC<RoomsAndPriceProps> = ({ room, onSelect, hotelName, slug, hotelId, totalCount, rate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const [showAllRates, setShowAllRates] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [propertyDetails, setPropertyDetails] = useState<PropertyDetails | null>(null);
  const hasFetchedDetails = useRef(false);

  const { roomRateKey, saveHotelValues } = useHotelContext();

  const toggleRowExpand = (index: number) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const { data: hotel } = useQuery(
    ['fetchHotelBySlug', slug],
    () => fetchHotelBySlug(slug),
    { enabled: !!slug }
  );

  useEffect(() => {
    if (!hasFetchedDetails.current && hotel!.destinationCode && hotelId) {
      hasFetchedDetails.current = true;
      getHotelDetails();
    }
  }, [hotel!.destinationCode, hotelId]);

  const getHotelDetails = async () => {
    try {
      const data = await fetchPropertyDetails(hotel!.destinationCode, hotelId);
      setPropertyDetails(data as PropertyDetails);
    } catch (error: any) {
      console.error('Error fetching hotel details:', error);
    }
  }

  const getMatchingAccommodationImage = (): string | null => {
    if (!propertyDetails) return null;

    const matchedAccommodation: Accommodation | undefined = propertyDetails.accommodations.find(
      (accommodation) => accommodation.code === room.code && accommodation.name === room.name
    );

    return matchedAccommodation?.featuredImage || null;
  };


  const handleEnquire = (deal: Deal) => {
    setSelectedDeal(deal);
    setIsModalOpen(false);
    onSelect(room, deal);
    saveHotelValues(
      room.code,
      room.name,
      hotelName,
      deal.info.accommodation?.mealPlan?.name ?? '',
      deal.price?.nett.toString(),
      deal.id,
      hotelId,
      deal.price?.gross.toString(),
      deal.price?.discount.toString(),
    );

    const isMobile = window.innerWidth < 768; // Assuming 768px is your mobile breakpoint

    setTimeout(() => {
      const elementId = isMobile ? 'inquire' : 'inquire2';
      const headerOffset = isMobile ? 140 : 50;

      const element = document.getElementById(elementId);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleRoomDetails = (deal: Deal) => {
    setSelectedDeal(deal);
    setIsRoomModalOpen(true);
    onSelect(room, deal);
  };

  const toggleShowAllRates = () => setShowAllRates(!showAllRates);

  const ratesToShow = showAllRates ? room.deals : room.deals.slice(0, 4);

  const featuredImage = getMatchingAccommodationImage();

  function calculateStartingPrice(deal: Deal) {
    // Ensure the deal and deal.price objects exist and have valid numbers
    const grossInitial = Number(deal.price?.gross) || 0;
    const discountProvided = Number(deal.price?.discount) || 0;

    // Determine the modified gross price and discount based on the provided discount
    let modifiedGrossPrice, discountApplied;

    if (discountProvided > 0) {
      modifiedGrossPrice = grossInitial * 1.04; // Increase by 4% if there's an existing discount
      discountApplied = discountProvided * 1.04;       // Use the provided discount
    } else {
      modifiedGrossPrice = grossInitial * 1.05; // Increase by 5% if there's no existing discount
      discountApplied = grossInitial * 0.01;    // Calculate 1% of the gross price as discount
    }

    // Calculate the final price after applying discount and exchange rate
    const finalPricePerPerson = (modifiedGrossPrice - discountApplied) / totalCount;
    const finalPrice = finalPricePerPerson * rate;

    // Return the final price, formatted to two decimal places
    return finalPrice.toFixed(2);
  }

  return (
    <>
      <div className={`border border-gray-200 rounded-lg shadow-sm my-4 md:mx-0 mx-4 overflow-hidden`}>
        {/* Room Header */}
        <div
          onClick={toggleExpand}
          className={`p-2 border-b block md:flex justify-between items-center transition ${isExpanded ? 'bg-halo-pink-bg' : 'bg-white hover:bg-halo-pink-bg'}`}
        >
          <div className='flex items-center'>
            {(featuredImage || '/general/No_Image.png') && (
              <img
                className="w-[100px] h-[100px] rounded mr-4 object-cover"
                src={featuredImage || '/general/No_Image.png'} // Fallback to default image if no featured image
                onClick={() => handleRoomDetails(room.deals[0])}
                alt={room.name}
              />
            )}
            <div>
              <h3
                className="text-lg font-semibold text-gray-800 cursor-pointer"
              >
                {room.name}
                {/* {roomCode === room.code && <span className="text-halo-pink"> (Selected)</span>} */}
              </h3>
              <span className="text-sm text-gray-500">
                From{' '}
                <span className="font-medium text-halo-pink">
                  £ {calculateStartingPrice(room.deals[0])} pp
                </span>
              </span>
            </div>
          </div>

          <div
            className="text-halo-pink text-sm cursor-pointer text-right"
            onClick={toggleExpand}
          >
            {isExpanded ? (
              <div className="flex items-center justify-end">
                See Less
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-up ml-2" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z" />
                </svg>
              </div>
            ) : (
              <div className="flex items-center justify-end text-sm">
                Select Meal Plan
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-down ml-2" viewBox="0 0 16 16">
                  <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="md:py-4 md:px-4 py-4 px-2 bg-white">
            {/* Table for Desktop */}
            <div className="hidden lg:block">
              <table className="min-w-full border-collapse text-left text-base">
                <thead>
                  <tr className="border-b text-gray-600">
                    <th className="py-2 px-4">Meal Plan</th>
                    <th className="py-2 px-4">Price</th>
                    <th className="py-2 px-4">Cancellation Policy</th>
                    <th className="py-2 px-4">Options</th>
                  </tr>
                </thead>
                <tbody>
                  {ratesToShow.map((deal, index) => (
                    <React.Fragment key={`deal-${index}`}>
                      <tr key={`deal-row-${index}`} className="hover:bg-gray-50 text-sm">
                        <td className="py-3 px-4">
                          <span className="text-gray-600 font-medium">
                            {deal.info.accommodation.mealPlan.name}
                          </span>
                        </td>
                        {/* <td className="py-3 px-4">
                          <span className={Object.keys(deal.info.transfer).length != 0 ? "text-gray-500" : "text-red-500"}>
                            {Object.keys(deal.info.transfer).length != 0 ? deal.info.transfer.arrival.name : '( Not Available )'}
                          </span>
                        </td>
                        <td>
                          <span className={Object.keys(deal.info.transfer).length != 0 ? "text-gray-500" : "text-red-500"}>
                            {Object.keys(deal.info.transfer).length != 0 ? deal.info.transfer.departure.name : '( Not Available )'}
                          </span>
                        </td> */}
                        <td className="py-3 px-4 hidden">
                          {/* Add 4% to the gross price */}
                          <span className="text-lg font-bold text-gray-800">
                            £ {(deal.price.gross * 1.04).toFixed(2)}
                          </span>
                        </td>
                        <td className="py-3 px-4 hidden">
                          <span className="text-base font-bold text-gray-800">
                            £ {deal.price.discount}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm font-bold text-gray-800 whitespace-nowrap">
                            £ {totalCount > 0 ? (
                              (deal.price.discount > 0 ?
                                ((((deal.price.gross * 1.04 - deal.price.discount * 1.04) / totalCount) * rate).toFixed(2)) :
                                ((((deal.price.gross * 1.05 - deal.price.gross * 0.01) / totalCount) * rate).toFixed(2))
                              )
                            ) : '-'
                            } <span className='text-sm'>pp</span>
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col space-y-1">
                            {deal.cancellationPolicies.length > 0 ? (
                              <div className="flex flex-col">
                                <div className="flex items-center">
                                  <img
                                    src={
                                      new Date(deal.cancellationPolicies[0].date) < new Date()
                                        ? '/featureIcons/cancellation-red.svg'
                                        : '/featureIcons/cancellation.svg'
                                    }
                                    className="w-3 h-3 mr-2"
                                    alt="Cancellation Icon"
                                  />
                                  <p
                                    className={`text-sm mb-0 font-normal ${new Date(deal.cancellationPolicies[0].date) < new Date()
                                      ? 'text-red-600'
                                      : 'text-green-600'
                                      }`}
                                  >
                                    {new Date(deal.cancellationPolicies[0].date) < new Date()
                                      ? 'Non-refundable'
                                      : `Free Cancellation upto ${new Date(deal.cancellationPolicies[0].date).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                      })}`}
                                  </p>
                                </div>
                              </div>

                            ) : (
                              <span className="text-sm text-green-600 mb-0 font-medium">Free Cancellation</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col space-y-1">
                            <a
                              href="#inquire2"
                              onClick={() => handleEnquire(deal)}
                              className={`mt-2 px-4 py-2 rounded-lg text-center ${roomRateKey === deal.id
                                ? 'bg-halo-pink'
                                : 'bg-[#ff3696] hover:bg-halo-pink'
                                } transition-colors text-white`}
                            >
                              {roomRateKey === deal.id ? 'Selected' : 'Select'}
                            </a>
                          </div>
                        </td>
                      </tr>

                      {expandedRow != index && (
                        <tr key={`expand-row-${index}`} className='border-b relative bottom-[10px]'>
                          <td className='text-center' colSpan={5}>
                            {
                              // Ensure both arrival and departure descriptions exist before rendering the button
                              (deal.info.transfer.arrival?.description && deal.info.transfer.departure?.description) ? (
                                <div className="text-right mt-2 px-4">
                                  <button
                                    onClick={() => toggleRowExpand(index)}
                                    className="text-[#0050b8] text-xs underline hover:text-blue-800"
                                  >
                                    {expandedRow === index ? 'Show Less' : 'More Details'}
                                  </button>
                                </div>
                              ) : null  // Render nothing if either description is unavailable
                            }
                          </td>
                        </tr>
                      )}

                      {expandedRow === index && (
                        <React.Fragment key={`expanded-${index}`}>
                          {/* <tr className="border-b">
                            <td className="py-1 px-4" colSpan={5}>
                              <p className="text-gray-900 font-medium">Accommodation</p>
                              <p className="text-gray-600 ml-4">{deal.info.accommodation.description}</p>
                            </td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-1 px-4" colSpan={5}>
                              <p className="text-gray-900 font-medium">Cancellation Policy</p>
                              {deal.cancellationPolicies.map((policy, policyIndex) => (
                                <p key={`policy-${index}-${policyIndex}`} className="text-green-600 ml-4">- {policy.description}</p>
                              ))}
                            </td>
                          </tr> */}
                          {Object.keys(deal.info.transfer).length !== 0 ? (
                            <tr>
                              <td colSpan={5} className="p-0">
                                <div className="bg-gray-100 rounded-lg w-full">
                                  <table className="w-full">
                                    <tbody>
                                      <tr>
                                        <td colSpan={5} className="py-1 px-4">
                                          <hr className="mt-2" />
                                          <div className="flex items-center mt-2 mb-2">
                                            <img src="/featureIcons/white-tag.svg" className="w-3 h-3 mr-2" />
                                            <p className="text-gray-600 font-semibold">Free Transfers</p>
                                          </div>
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="py-1 px-3" colSpan={2}>
                                          <div>
                                            <div className="flex items-center">
                                              <img src="/featureIcons/1.svg" className="w-5 h-5 mr-1" />
                                              <p className="text-gray-600 font-medium text-sm">Arrival Transfer</p>
                                            </div>
                                            <div className="ml-[24px] mb-2">
                                              <p className="text-gray-500 text-xs">{deal.info.transfer.arrival.description}</p>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="py-1 px-3" colSpan={2}>
                                          <div>
                                            <div className="flex items-center">
                                              <img src="/featureIcons/3.svg" className="w-5 h-5 mr-1" />
                                              <p className="text-gray-600 font-medium text-sm">Departure Transfer</p>
                                            </div>
                                            <div className="ml-[24px] mb-2">
                                              <p className="text-gray-500 text-xs">{deal.info.transfer.departure.description}</p>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          ) : ''}

                          {/* {deal.extraBenefits && deal.extraBenefits.length > 0 && (
                            <tr key={`extraBenefits-${index}`} className="border-b">
                              <td className="py-1 px-4" colSpan={5}>
                                <p className="text-gray-900 font-medium">Extra Benefits</p>
                                {deal.extraBenefits.map((benefit, benefitIndex) => (
                                  <p key={`benefit-${index}-${benefitIndex}`} className="text-gray-600 ml-4">{benefit.description}</p>
                                ))}
                              </td>
                            </tr>
                          )} */}

                          {/* {deal.freeUpgrades && deal.freeUpgrades.length > 0 && (
                            <tr key={`freeUpgrades-${index}`} className="border-b">
                              <td className="py-1 px-4" colSpan={5}>
                                <p className="text-gray-900 font-medium">Free Upgrades</p>
                                {deal.freeUpgrades.map((upgrade, upgradeIndex) => (
                                  <p key={`upgrade-${index}-${upgradeIndex}`} className="text-gray-600 ml-4">{upgrade.description}</p>
                                ))}
                              </td>
                            </tr>
                          )} */}

                          {/* {deal.offers && deal.offers.length > 0 && (
                            <tr key={`offers-${index}`} className=''>
                              <td className='py-1 px-4' colSpan={5}>
                                <p className="text-gray-900 font-medium">Offers</p>
                                {deal.offers.map((offer, offerIndex) => (
                                  <p key={`offer-${index}-${offerIndex}`} className='text-halo-pink'>{offer.days.length > 0 ? offer.name : ''}</p>
                                ))}
                                {deal.offers.map((offer, offerIndex) =>
                                  offer.days.map((day, dayIndex) => (
                                    <p key={`offerDay-${index}-${offerIndex}-${dayIndex}`} className="text-gray-600 ml-4">
                                      {day.date} - {day.description}
                                    </p>
                                  ))
                                )}
                              </td>
                            </tr>
                          )} */}
                        </React.Fragment>
                      )}

                      {expandedRow === index && (
                        <tr key={`expand-end-row-${index}`} className='border-b'>
                          <td className='text-right px-4' colSpan={5}>
                            <button
                              onClick={() => toggleRowExpand(index)}
                              className="text-[#0050b8] text-xs underline mb-2 hover:text-blue-800"
                            >
                              {expandedRow === index ? 'Less Details' : 'More Details'}
                            </button>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>



            {/* Mobile View */}
            <div className="block lg:hidden w-full">
              {ratesToShow.map((deal, index) => (
                <div key={`deal-mobile-${index}`} className="border-b py-4 px-2 flex flex-col space-y-2">
                  {/* Meal Plan */}
                  <div>
                    <span className='text-gray-800 font-semibold underline'>Meal Plan</span>
                  </div>
                  <div className='flex justify-between items-center'> {/* Use items-center to center-align children vertically */}
                    <span className="text-sm text-gray-600 font-medium w-3/4"> {/* Use a width class to prevent overflow */}
                      {deal.info.accommodation.mealPlan.name}
                    </span>
                    <div className="text-sm font-bold text-gray-800 whitespace-nowrap"> {/* Prevent wrapping of price */}
                      £ {totalCount > 0 ? (
                        deal.price.discount > 0 ?
                          ((((deal.price.gross * 1.04 - deal.price.discount * 1.04) / totalCount) * rate).toFixed(2)) :
                          ((((deal.price.gross * 1.05 - deal.price.gross * 0.01) / totalCount) * rate).toFixed(2))
                      ) : '-'
                      } pp
                    </div>
                  </div>
                  <div className='flex justify-between items-center'>
                    <div className='flex items-center'>
                      <img
                        src={
                          new Date(deal.cancellationPolicies[0].date) < new Date()
                            ? '/featureIcons/cancellation-red.svg'
                            : '/featureIcons/cancellation.svg'
                        }
                        className="w-3 h-3 mr-2"
                        alt="Cancellation Icon"
                      />
                      {deal.cancellationPolicies.length > 0 ? (
                        <p
                          className={`text-xs text-green-600 mb-0 font-normal ${new Date(deal.cancellationPolicies[0].date) < new Date()
                            ? 'text-red-600'
                            : 'text-green-600'
                            }`}
                        >
                          {new Date(deal.cancellationPolicies[0].date) < new Date()
                            ? 'Non-refundable'
                            : `Free Cancellation upto ${new Date(deal.cancellationPolicies[0].date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })}`}
                        </p>
                      ) : (
                        <span className="text-xs text-green-600 mb-0 font-medium">Free Cancellation</span>
                      )}
                    </div>
                    <a
                      href="#inquire"
                      onClick={() => handleEnquire(deal)}
                      className={`px-4 py-2 rounded-lg text-center ${roomRateKey === deal.id ? 'bg-halo-pink hover:bg-pink-800' : 'bg-pink-400 hover:bg-pink-500'} transition-colors text-white`}
                    >
                      {roomRateKey === deal.id ? 'Selected' : 'Select'}
                    </a>
                  </div>

                  {/* Expanded Details */}
                  {expandedRow === index && (
                    <>
                      {/* Accommodation Details */}
                      {/* <div className="pt-2">
                        <p className="text-gray-900 font-semibold">Accommodation</p>
                        {deal.info.accommodation.description.flat().join('\n').split('\n').map((line, lineIndex) => (
                          <p key={lineIndex} className="text-gray-600">{line}</p>
                        ))}
                      </div> */}

                      {/* Transfer Details */}
                      {Object.keys(deal.info.transfer).length != 0 ?
                        <div className="pt-2 bg-gray-50 p-2">
                          <div className="flex items-center">
                            <img src="/featureIcons/white-tag.svg" className="w-3 h-3 mr-2 ml-1" />
                            <p className="text-gray-800 font-semibold text-base">Free Transfers</p>
                          </div>
                          <div className=''>
                            <div className="flex items-center">
                              <img src="/featureIcons/1.svg" className="w-5 h-5 mr-2" />
                              <div>
                                <p className="text-gray-700 font-medium underline text-sm">Arrival</p>
                                <p className="text-gray-600 text-xs">{deal.info.transfer.arrival.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center mt-1">
                              <img src="/featureIcons/3.svg" className="w-5 h-5 mr-2" />
                              <div>
                                <p className="text-gray-700 font-medium underline text-sm">Departure</p>
                                <p className="text-gray-600 text-xs">{deal.info.transfer.departure.description}</p>
                              </div>
                            </div>

                          </div>
                        </div> : ''}

                      {/* Cancellation Policy */}
                      {/* <div className="pt-2">
                        <p className="text-gray-900 font-semibold">Cancellation Policy</p>
                        {deal.cancellationPolicies.length > 0 ? (
                          deal.cancellationPolicies.flat().map((policy, policyIndex) => (
                            <p key={`policy-${index}-${policyIndex}`} className="text-green-600">
                              {policy.description}
                            </p>
                          ))
                        ) : (
                          <span className="text-xs text-red-500">Non-refundable</span>
                        )}
                      </div> */}

                      {/* Extra Benefits */}
                      {/* {deal.extraBenefits && deal.extraBenefits.length > 0 && (
                        <div className="pt-2">
                          <p className="text-gray-900 font-semibold">Extra Benefits</p>
                          {deal.extraBenefits.map((benefit, benefitIndex) => (
                            <p key={benefitIndex} className="text-gray-600">{benefit.description}</p>
                          ))}
                        </div>
                      )} */}

                      {/* Free Upgrades */}
                      {/* {deal.freeUpgrades && deal.freeUpgrades.length > 0 && (
                        <div className="pt-2">
                          <p className="text-gray-900 font-semibold">Free Upgrades</p>
                          {deal.freeUpgrades.map((upgrade, upgradeIndex) => (
                            <p key={upgradeIndex} className="text-gray-600">{upgrade.description}</p>
                          ))}
                        </div>
                      )} */}

                      {/* Offers */}
                      {/* {deal.offers && deal.offers.length > 0 && (
                        <div className="pt-2">
                          <p className="text-gray-900 font-semibold">Offers</p>
                          {deal.offers.map((offer, offerIndex) => (
                            <div key={offerIndex}>
                              {offer.days.length > 0 && (
                                <p className="text-halo-pink">{offer.name}</p>
                              )}
                              {offer.days.map((day, dayIndex) => (
                                <p key={dayIndex} className="text-gray-600">
                                  {day.date} - {day.description}
                                </p>
                              ))}
                            </div>
                          ))}
                        </div>
                      )} */}
                    </>
                  )}

                  {/* Show More/Less Toggle */}
                  {
                    // Ensure both arrival and departure descriptions exist before rendering the button
                    (deal.info.transfer.arrival?.description && deal.info.transfer.departure?.description) ? (
                      <div className="text-center mt-2">
                        <button
                          onClick={() => toggleRowExpand(index)}
                          className="text-[#0050b8] text-sm underline"
                        >
                          {expandedRow === index ? 'Less Details' : 'More Details'}
                        </button>
                      </div>
                    ) : null  // Render nothing if either description is unavailable
                  }
                </div>
              ))}
            </div>


            {/* View More Button */}
            {room.deals.length > 4 && (
              <div className="text-center mt-4">
                <button
                  onClick={toggleShowAllRates}
                  className="px-4 py-2 text-sm text-halo-pink bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {showAllRates ? 'View Less' : 'View More Plans'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isModalOpen && selectedDeal && (
        <EnquireModal
          room={room}
          deal={selectedDeal}
          onClose={() => setIsModalOpen(false)}
          hotelName={hotelName}
          slug={slug}
        />
      )}

      {isRoomModalOpen && selectedDeal && (
        <RoomDetailsModal
          onClose={() => setIsRoomModalOpen(false)}
          room={room}
          deal={selectedDeal}
          hotelName={hotelName}
          hotelId={hotelId}
          destinationCode={hotel!.destinationCode}
        />
      )}
    </>
  );
};

export default RoomsAndPrice;
